import React from "react";
import moment from "moment";
import { formatOrderDate } from "../../../../lib/formatOrderDate";

import { Order, OrderDetailsInfo, DumpEvent } from "../../../../models";
import { triplePSDatasource } from "../../../../datasource";
import { PhotoThumb } from "../PhotoThumb/PhotoThumb";
import { InvoicesCard } from "../InvoicesCard/InvoicesCard";
import { OrderProfitCard } from "../OrderProfitCard/OrderProfitCard";

import "./OrderTimeline.scss";

interface Props {
  order: Order;
  orderDetails?: OrderDetailsInfo;
}

const fmtDateTime = (iso?: string) =>
  iso ? moment(iso).format("MMM D, YYYY · h:mm A") : undefined;

const isComplete = (status?: string) => {
  const s = status?.toLowerCase();
  return s === "complete" || s === "completed";
};

// Maps a status string to a chip style. Completion states are green; anything
// pending/extracted is amber; failed is red.
function statusChip(status?: string): { cls: string; label: string } | null {
  if (!status) return null;
  const s = status.toLowerCase();
  if (s === "complete" || s === "completed" || s === "confirmed")
    return { cls: "ok", label: status };
  if (s === "failed" || s === "canceled" || s === "cancelled")
    return { cls: "err", label: status };
  if (s === "scheduled") return { cls: "sched", label: status };
  return { cls: "pending", label: status };
}

function Chip({ status }: { status?: string }) {
  const c = statusChip(status);
  if (!c) return null;
  return <span className={`tl-chip ${c.cls}`}>{c.label}</span>;
}

export function OrderTimeline({ order, orderDetails }: Props) {
  const [dumpEvents, setDumpEvents] = React.useState<DumpEvent[] | null>(null);

  // Lazily pull this order's dump events (yard, tonnage, cost, receipt) once.
  React.useEffect(() => {
    let active = true;
    triplePSDatasource
      .getDumpEvents({ orderId: order.id }, { count: 25, offset: 0 })
      .then((events) => {
        if (active) setDumpEvents(events);
      })
      .catch(() => {
        if (active) setDumpEvents([]);
      });
    return () => {
      active = false;
    };
  }, [order.id]);

  const delivery = orderDetails?.delivery;
  const pickup = orderDetails?.pickup;

  return (
    <div className="order-timeline">
      {/* DELIVERY */}
      <div className="tl-node">
        <div className="tl-dot delivery">🚚</div>
        <div className="tl-card">
          <div className="tl-meta">
            <div className="tl-toprow">
              <span className="tl-stage">Delivery</span>
              <Chip status={delivery?.status} />
            </div>
            <div className="tl-line">
              {fmtDateTime(delivery?.completedAt) ??
                `Scheduled ${formatOrderDate(order.deliveryDate)}`}
              {delivery?.driverName && (
                <>
                  {" · "}
                  <span className="muted">Driver</span> <b>{delivery.driverName}</b>
                </>
              )}
            </div>
            <div className="tl-subline">
              {order.address}
              {order.city ? `, ${order.city}` : ""}
            </div>
          </div>
          {delivery?.photo_s3_location && delivery.delivery_id ? (
            <PhotoThumb
              label="View photo"
              modalTitle="Delivery Photo"
              fetchImage={() =>
                triplePSDatasource.getDeliveryPhoto(delivery.delivery_id as string)
              }
            />
          ) : (
            <div className="photo-thumb-ghost" title="No delivery photo">
              <span className="icon">📷</span>
              <span className="lbl">No photo</span>
            </div>
          )}
        </div>
      </div>

      {/* PICKUP */}
      <div className="tl-node">
        <div className="tl-dot pickup">↩︎</div>
        <div className="tl-card">
          <div className="tl-meta">
            <div className="tl-toprow">
              <span className="tl-stage">Pickup</span>
              <Chip status={pickup?.status} />
            </div>
            <div className="tl-line">
              {fmtDateTime(pickup?.completedAt) ??
                `Scheduled ${formatOrderDate(order.pickupDate)}`}
              {pickup?.driverName && (
                <>
                  {" · "}
                  <span className="muted">Driver</span> <b>{pickup.driverName}</b>
                </>
              )}
            </div>
            {isComplete(pickup?.status) && (
              <div className="tl-subline">Container hauled from site</div>
            )}
          </div>
          {pickup?.photo_s3_location && pickup.pickup_id ? (
            <PhotoThumb
              label="View photo"
              modalTitle="Pickup Photo"
              fetchImage={() =>
                triplePSDatasource.getPickupPhoto(pickup.pickup_id as string)
              }
            />
          ) : (
            <div className="photo-thumb-ghost" title="No pickup photo">
              <span className="icon">📷</span>
              <span className="lbl">No photo</span>
            </div>
          )}
        </div>
      </div>

      {/* DUMP */}
      <div className="tl-node">
        <div className="tl-dot dump">♻︎</div>
        <div className="tl-card dump-card">
          <div className="tl-meta">
            <div className="tl-toprow">
              <span className="tl-stage">Dump</span>
              {dumpEvents && dumpEvents.length > 0 && (
                <Chip status={dumpEvents[0].extractionStatus} />
              )}
            </div>
            {dumpEvents === null && <div className="tl-subline">Loading…</div>}
            {dumpEvents !== null && dumpEvents.length === 0 && (
              <div className="tl-subline">No dump recorded for this order.</div>
            )}
            {dumpEvents?.map((d) => (
              <div className="dump-row" key={d.id}>
                <div className="dump-row-info">
                  <div className="tl-line">
                    {fmtDateTime(d.createdAt)}
                    {d.dumpYardName && (
                      <>
                        {" · "}
                        <b>{d.dumpYardName}</b>
                      </>
                    )}
                  </div>
                  <div className="tl-subline">
                    {d.weight != null && <>{d.weight} tons</>}
                    {d.vendorTicketNumber && <> · ticket #{d.vendorTicketNumber}</>}
                    {d.paidAmount != null && (
                      <>
                        {" · "}
                        <b>${d.paidAmount.toFixed(2)}</b> dump fee
                      </>
                    )}
                  </div>
                </div>
                {d.receiptS3Location && (
                  <PhotoThumb
                    label="View receipt"
                    kind="receipt"
                    modalTitle="Dump Receipt"
                    fetchImage={() => triplePSDatasource.getDumpEventReceipt(d.id)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* BILLING: invoices + profit */}
      <div className="tl-node billing">
        <div className="tl-dot billing">$</div>
        <div className="tl-card billing-card">
          <InvoicesCard order={order} />
          <OrderProfitCard orderId={order.id} variant="dark" />
        </div>
      </div>
    </div>
  );
}
